import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/journal";
import { Button } from "@/components/ui/button";
import { ArrowClockwise, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export const SyncStatusPanel = ({ open }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/wallet/sync/status").then(({ data }) => setStatus(data)).catch(() => {});

  useEffect(() => {
    if (open) load();
  }, [open]);

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/sync/reset");
      toast.success(`Sync error cleared on ${data.reset} portfolio(s)`);
      await load();
    } catch {
      toast.error("Failed to reset sync state");
    } finally {
      setBusy(false);
    }
  };

  const wallets = (status?.portfolios || []).filter((p) => p.address);

  return (
    <div className="space-y-3 border-t border-[#1F1F1F] pt-5" data-testid="sync-status-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Wallet Sync Status</p>
        <Button
          data-testid="sync-reset-btn"
          onClick={reset}
          disabled={busy}
          className="h-7 rounded-none border border-[#1F1F1F] bg-transparent px-2 text-[11px] text-zinc-400 hover:bg-[#111111] hover:text-white"
        >
          <ArrowClockwise size={12} className="mr-1.5" /> Reset errors
        </Button>
      </div>

      {wallets.length === 0 ? (
        <p className="font-mono text-[11px] text-zinc-600">No on-chain wallets linked yet.</p>
      ) : (
        wallets.map((p) => (
          <div key={p.id} className="border border-[#1F1F1F] p-3" data-testid={`sync-status-${p.id}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-zinc-200">
                {p.name} <span className="text-zinc-600">· {p.network}</span>
              </span>
              {p.last_sync_error ? (
                <WarningCircle size={15} className="shrink-0 text-[#EF4444]" />
              ) : (
                <CheckCircle size={15} className="shrink-0 text-[#4ADE80]" />
              )}
            </div>
            <p className="mt-1 break-all font-mono text-[10px] text-zinc-600">{p.address}</p>
            <p className="mt-1 font-mono text-[10px] text-zinc-500">
              {p.last_sync_at
                ? `Last sync ${fmtDate(p.last_sync_at, true)} · ${p.last_sync_imported ?? 0} imported`
                : "Never synced"}
            </p>
            {p.last_sync_error && (
              <p className="mt-1 font-mono text-[10px] text-[#EF4444]">{p.last_sync_error}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
};
